<template>
  <section class="mx-auto flex min-h-[70vh] max-w-5xl flex-col gap-8 px-6 py-16">
    <div class="flex flex-col gap-6 rounded-3xl border border-white/10 bg-midnight-800/60 p-6 shadow-glow sm:p-8">
      <div
        v-if="status === 'banned'"
        class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 text-sm text-rose-100"
      >
        This account has been banned. Access is restricted until a moderator lifts the ban.
      </div>
      <div class="flex flex-col gap-6 sm:flex-row sm:items-center">
        <div class="flex items-center gap-4">
          <div class="h-24 w-24 overflow-hidden rounded-3xl border border-white/10 bg-midnight-900/70">
            <img
              v-if="displayAvatarUrl"
              :src="displayAvatarUrl"
              alt="Profile avatar"
              class="h-full w-full object-cover"
              referrerpolicy="no-referrer"
              crossorigin="anonymous"
            />
            <div v-else class="flex h-full w-full items-center justify-center text-3xl font-semibold text-steel-200">
              {{ initials }}
            </div>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.35em] text-steel-400">Member Profile</p>
            <h1 class="mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
              {{ fullName || 'DRVN Member' }}
            </h1>
            <p class="text-sm text-steel-400">{{ email }}</p>
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-3 sm:ml-auto">
          <router-link
            class="inline-flex items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10"
            :to="`/u/${handle || 'member'}`"
          >
            View public profile
          </router-link>
          <label class="inline-flex cursor-pointer items-center gap-3 rounded-2xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:border-white/30 hover:bg-white/10">
            <input class="hidden" type="file" accept="image/*" @change="handleAvatarUpload" />
            Upload photo
          </label>
        </div>
      </div>

      <div class="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div class="space-y-5">
          <label class="block text-sm text-steel-200">
            Display name
            <input
              v-model.trim="fullName"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Your name"
            />
          </label>

          <label class="block text-sm text-steel-200">
            Handle
            <input
              v-model.trim="handle"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="drvnmember"
            />
          </label>

          <label class="block text-sm text-steel-200">
            Instagram
            <input
              v-model.trim="instagramHandle"
              type="text"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="yourhandle"
            />
          </label>

          <div class="grid gap-4 sm:grid-cols-2">
            <div>
              <label for="country" class="text-sm text-steel-200">Choose a country:</label>
              <select
                id="country"
                name="country"
                v-model="country"
                class="mt-3 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              >
                <option disabled value="">Select a country</option>
                <option v-for="option in countryOptions" :key="option.code" :value="option.code">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <label class="block text-sm text-steel-200">
              City
              <input
                v-model.trim="city"
                type="text"
                class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                placeholder="Your city"
              />
            </label>
          </div>

          <label class="block text-sm text-steel-200">
            Bio
            <textarea
              v-model.trim="bio"
              rows="5"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Tell the community about your build, your drive, and your story."
            ></textarea>
          </label>

          <label class="block text-sm text-steel-200">
            Pinned status
            <input
              v-model.trim="pinnedStatus"
              type="text"
              maxlength="80"
              class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-base text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              placeholder="Example: Available for collabs"
            />
          </label>

          <label class="block text-sm text-steel-200">
            Interests & Tags
            <p class="mt-2 text-xs text-steel-400 mb-3">Select tags that describe you</p>
            <div class="grid gap-2 grid-cols-2 sm:grid-cols-3">
              <label v-for="tag in availableTags" :key="tag" class="flex items-center gap-2 p-3 rounded-2xl border border-white/10 bg-midnight-900/60 hover:border-white/20 cursor-pointer transition">
                <input
                  type="checkbox"
                  :value="tag"
                  v-model="selectedTags"
                  class="w-4 h-4 accent-neon-400"
                />
                <span class="text-sm text-steel-200">{{ tag }}</span>
              </label>
            </div>
          </label>

          <!-- New post moved to Feed view (floating button + modal) -->
        </div>

        <div class="space-y-5 rounded-2xl border border-white/10 bg-midnight-900/40 p-5">
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Stats</p>
            <p class="mt-2 text-sm text-steel-200">Member since {{ memberSince }}</p>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Role</p>
            <div class="mt-2">
              <span class="inline-block rounded-full px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] border" :class="roleBadgeClass">
                {{ roleLabel }}
              </span>
            </div>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Tournament</p>
            <div class="mt-2">
              <span class="inline-block rounded-full px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] border" :class="championBadgeClass">
                {{ championsLeagueWinner ? 'Champions League Winner' : 'No championship badge yet' }}
              </span>
            </div>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Handles</p>
            <p class="mt-2 text-sm text-steel-200">@{{ handle }}</p>
          </div>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-steel-400">Status</p>
            <p class="mt-2 text-sm text-steel-200">{{ statusLabel }}</p>
          </div>
        </div>
      </div>

      <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="post in myPosts"
          :key="post.id"
          class="group overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/60 transition hover:border-white/20"
        >
        <router-link :to="`/post/${post.id}`" class="block">
          <div class="relative w-full aspect-square">
            <transition :name="post.mediaDirection === 'prev' ? 'media-swipe-prev' : 'media-swipe-next'" mode="out-in">
              <img
                :key="postMediaUrl(post)"
                :src="postMediaUrl(post)"
                alt="Post"
                class="h-full w-full object-cover"
                :style="{ objectPosition: postMediaPosition(post) }"
              />
            </transition>
            <button
              v-if="post.mediaUrls?.length > 1"
              type="button"
              class="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/40 px-2 py-1 text-xs text-white"
              @click.prevent="prevMedia(post)"
            >
              Prev
            </button>
            <button
              v-if="post.mediaUrls?.length > 1"
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-black/40 px-2 py-1 text-xs text-white"
              @click.prevent="nextMedia(post)"
            >
              Next
            </button>
          </div>
          <div class="p-4">
            <p class="text-sm text-steel-200/90">{{ post.caption || 'No caption' }}</p>
            <p v-if="post.description" class="mt-2 text-xs text-steel-400">{{ post.description }}</p>
            <p v-if="post.modifications" class="mt-2 text-xs text-steel-400">Mods: {{ post.modifications }}</p>
            <p class="mt-2 text-xs text-steel-400">Likes: {{ post.likeCount }}</p>
            <div v-if="post.engine || post.hp_stock || post.hp_tuned || post.year" class="mt-2 text-xs text-steel-400">
              <p v-if="post.engine">Engine: {{ post.engine }}</p>
              <p v-if="post.hp_stock">HP Stock: {{ post.hp_stock }}</p>
              <p v-if="post.hp_tuned">HP Tuned: {{ post.hp_tuned }}</p>
              <p v-if="post.year">Year: {{ post.year }}</p>
            </div>
          </div>
        </router-link>
          <div class="px-4 pb-4">
            <button
              type="button"
              class="w-full rounded-2xl border border-rose-500/40 bg-rose-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-rose-100 transition hover:border-rose-500/70 hover:bg-rose-500/20"
              @click="showDeletePostConfirm = true; postToDelete = post"
            >
              Delete post
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col items-center gap-4 sm:flex-row sm:items-center sm:justify-start">
        <button
          type="button"
          class="w-full rounded-2xl bg-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
          :disabled="isSaving"
          @click="saveProfile"
        >
          {{ isSaving ? 'Saving...' : 'Save changes' }}
        </button>
        <button
          type="button"
          class="w-full rounded-2xl border border-rose-500/40 bg-rose-500/10 px-6 py-3 text-sm font-semibold uppercase tracking-[0.3em] text-rose-100 transition hover:border-rose-500/70 hover:bg-rose-500/20 sm:w-auto"
          :disabled="isDeleting"
          @click="showDeleteConfirm = true"
        >
          Delete profile
        </button>
        <p v-if="statusMessage" class="text-sm text-steel-300">{{ statusMessage }}</p>
      </div>

      <p v-if="errorMessage" class="rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ errorMessage }}
      </p>
    </div>

    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-6"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete profile?</h3>
        <p class="mt-3 text-sm text-steel-300">
          This will remove your profile and posts. This action cannot be undone.
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="showDeleteConfirm = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400"
            :disabled="isDeleting"
            @click="deleteProfile"
          >
            {{ isDeleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDeletePostConfirm"
      class="fixed inset-0 z-40 flex items-center justify-center bg-black/60 px-6"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete post?</h3>
        <p class="mt-3 text-sm text-steel-300">
          This will permanently remove the post. This action cannot be undone.
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="showDeletePostConfirm = false; postToDelete = null"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400"
            @click="confirmDeletePost"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'

export default {
  name: 'ProfileView',
  data() {
    return {
      email: '',
      fullName: '',
      bio: '',
      avatarUrl: '',
      avatarPath: '',
      avatarPreviewUrl: '',
      role: '',
      championsLeagueWinner: false,
      status: '',
      memberSince: '',
      isSaving: false,
      statusMessage: '',
      errorMessage: '',
      handle: '',
      instagramHandle: '',
      pinnedStatus: '',
      country: '',
      city: '',
      selectedTags: [],
      availableTags: [
        'Car Enthusiast',
        'Photographer',
        'Mechanic',
        'Customizer',
        'Collector',
        'Racer',
        'Builder',
        'Tuner',
      ],
      countryOptions: [
        { code: 'US', label: 'USA', flag: '🇺🇸' },
        { code: 'GB', label: 'UK', flag: '🇬🇧' },
        { code: 'CA', label: 'Canada', flag: '🇨🇦' },
        { code: 'JP', label: 'Japan', flag: '🇯🇵' },
        { code: 'KR', label: 'Korea', flag: '🇰🇷' },
        { code: 'BR', label: 'Brazil', flag: '🇧🇷' },
        { code: 'AL', label: 'Albania', flag: '🇦🇱' },
        { code: 'AD', label: 'Andorra', flag: '🇦🇩' },
        { code: 'AM', label: 'Armenia', flag: '🇦🇲' },
        { code: 'AT', label: 'Austria', flag: '🇦🇹' },
        { code: 'AZ', label: 'Azerbaijan', flag: '🇦🇿' },
        { code: 'BY', label: 'Belarus', flag: '🇧🇾' },
        { code: 'BE', label: 'Belgium', flag: '🇧🇪' },
        { code: 'BA', label: 'Bosnia and Herzegovina', flag: '🇧🇦' },
        { code: 'BG', label: 'Bulgaria', flag: '🇧🇬' },
        { code: 'HR', label: 'Croatia', flag: '🇭🇷' },
        { code: 'CY', label: 'Cyprus', flag: '🇨🇾' },
        { code: 'CZ', label: 'Czechia', flag: '🇨🇿' },
        { code: 'DK', label: 'Denmark', flag: '🇩🇰' },
        { code: 'EE', label: 'Estonia', flag: '🇪🇪' },
        { code: 'FI', label: 'Finland', flag: '🇫🇮' },
        { code: 'FR', label: 'France', flag: '🇫🇷' },
        { code: 'GE', label: 'Georgia', flag: '🇬🇪' },
        { code: 'DE', label: 'Germany', flag: '🇩🇪' },
        { code: 'GR', label: 'Greece', flag: '🇬🇷' },
        { code: 'HU', label: 'Hungary', flag: '🇭🇺' },
        { code: 'IS', label: 'Iceland', flag: '🇮🇸' },
        { code: 'IE', label: 'Ireland', flag: '🇮🇪' },
        { code: 'IT', label: 'Italy', flag: '🇮🇹' },
        { code: 'KZ', label: 'Kazakhstan', flag: '🇰🇿' },
        { code: 'XK', label: 'Kosovo', flag: '🇽🇰' },
        { code: 'LV', label: 'Latvia', flag: '🇱🇻' },
        { code: 'LI', label: 'Liechtenstein', flag: '🇱🇮' },
        { code: 'LT', label: 'Lithuania', flag: '🇱🇹' },
        { code: 'LU', label: 'Luxembourg', flag: '🇱🇺' },
        { code: 'MT', label: 'Malta', flag: '🇲🇹' },
        { code: 'MD', label: 'Moldova', flag: '🇲🇩' },
        { code: 'MC', label: 'Monaco', flag: '🇲🇨' },
        { code: 'ME', label: 'Montenegro', flag: '🇲🇪' },
        { code: 'NL', label: 'Netherlands', flag: '🇳🇱' },
        { code: 'MK', label: 'North Macedonia', flag: '🇲🇰' },
        { code: 'NO', label: 'Norway', flag: '🇳🇴' },
        { code: 'PL', label: 'Poland', flag: '🇵🇱' },
        { code: 'PT', label: 'Portugal', flag: '🇵🇹' },
        { code: 'RO', label: 'Romania', flag: '🇷🇴' },
        { code: 'RU', label: 'Russia', flag: '🇷🇺' },
        { code: 'SM', label: 'San Marino', flag: '🇸🇲' },
        { code: 'RS', label: 'Serbia', flag: '🇷🇸' },
        { code: 'SK', label: 'Slovakia', flag: '🇸🇰' },
        { code: 'SI', label: 'Slovenia', flag: '🇸🇮' },
        { code: 'ES', label: 'Spain', flag: '🇪🇸' },
        { code: 'SE', label: 'Sweden', flag: '🇸🇪' },
        { code: 'CH', label: 'Switzerland', flag: '🇨🇭' },
        { code: 'TR', label: 'Turkey', flag: '🇹🇷' },
        { code: 'UA', label: 'Ukraine', flag: '🇺🇦' },
        { code: 'GB', label: 'United Kingdom', flag: '🇬🇧' },
        { code: 'VA', label: 'Vatican City', flag: '🇻🇦' },
      ],
      myPosts: [],
      showDeleteConfirm: false,
      showDeletePostConfirm: false,
      postToDelete: null,
      isDeleting: false,
    }
  },
  computed: {
    statusLabel() {
      return this.status === 'banned' ? 'Banned' : 'Active'
    },
    roleLabel() {
      return (this.role || 'member')
    },
    roleBadgeClass() {
      const role = (this.role || 'member').toLowerCase()
      if (role === 'admin') return 'bg-rose-500/10 border-rose-500/30 text-rose-200'
      if (role === 'moderator') return 'bg-violet-400/10 border-violet-400/30 text-violet-200'
      return 'bg-neon-400/10 border-neon-400/20 text-neon-400'
    },
    championBadgeClass() {
      return this.championsLeagueWinner
        ? 'bg-amber-300/20 border-amber-300/50 text-amber-100'
        : 'bg-white/5 border-white/10 text-steel-400'
    },
    initials() {
      if (!this.fullName) return 'D'
      return this.fullName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((chunk) => chunk[0].toUpperCase())
        .join('')
    },
    displayAvatarUrl() {
      if (this.avatarPreviewUrl) return this.avatarPreviewUrl
      return getAvatarUrl({ avatarPath: this.avatarPath, avatarUrl: this.avatarUrl })
    },
  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    const session = data?.session
    if (!session?.user) {
      this.$router.push('/login')
      return
    }

    const user = session.user
    this.email = user.email || ''
    this.memberSince = new Date(user.created_at).toLocaleDateString()

    const { data: profile } = await supabase
      .from('profiles')
      .select('handle, full_name, bio, avatar_url, avatar_path, role, champions_league_winner, status, country, city, instagram_handle, tags, pinned_status')
      .eq('id', user.id)
      .maybeSingle()

    this.fullName = profile?.full_name || user.user_metadata?.full_name || ''
    this.bio = profile?.bio || user.user_metadata?.bio || ''
    this.avatarUrl = profile?.avatar_url || user.user_metadata?.avatar_url || ''
    this.avatarPath = profile?.avatar_path || ''
    this.handle = profile?.handle || user.user_metadata?.handle || ''
    this.instagramHandle = profile?.instagram_handle || ''
    this.pinnedStatus = profile?.pinned_status || ''
    this.role = profile?.role || 'member'
    this.championsLeagueWinner = Boolean(profile?.champions_league_winner)
    this.status = profile?.status || 'active'
    this.country = profile?.country || ''
    this.city = profile?.city || ''
    this.selectedTags = profile?.tags || []

    await this.fetchMyPosts(user.id)

  },
  methods: {
    postMediaUrl(post) {
      return post.mediaUrls?.[post.mediaIndex] || post.imageUrl || ''
    },
    postMediaPosition(post) {
      const offset = post.mediaOffsets?.[post.mediaIndex] || post.mediaOffsets?.[0] || { x: 50, y: 50 }
      return `${offset.x}% ${offset.y}%`
    },
    async convertImageToWebp(file, { maxWidth, quality = 0.82 }) {
      const image = await new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('Image decode failed.'))
        img.src = URL.createObjectURL(file)
      })

      const originalWidth = image.width || 1
      const originalHeight = image.height || 1
      const scale = Math.min(1, maxWidth / originalWidth)
      const targetWidth = Math.max(1, Math.round(originalWidth * scale))
      const targetHeight = Math.max(1, Math.round(originalHeight * scale))

      const canvas = document.createElement('canvas')
      canvas.width = targetWidth
      canvas.height = targetHeight
      const ctx = canvas.getContext('2d')
      ctx.drawImage(image, 0, 0, targetWidth, targetHeight)

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', quality))
      if (!blob) throw new Error('Image conversion failed.')

      return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' })
    },
    async fetchMyPosts(userId) {
      const { data, error } = await supabase
        .from('posts')
        .select('id, caption, description, modifications, engine, hp_stock, hp_tuned, year, image_url, image_path, image_offset_x, image_offset_y, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) return

      const posts = (data || []).map((post) => ({
        ...post,
        imageUrl: post.image_path
          ? supabase.storage.from('posts').getPublicUrl(post.image_path).data?.publicUrl
          : post.image_url,
        mediaUrls: [],
        mediaOffsets: [
          {
            x: Number.isFinite(post.image_offset_x) ? post.image_offset_x : 50,
            y: Number.isFinite(post.image_offset_y) ? post.image_offset_y : 50,
          },
        ],
        mediaIndex: 0,
        mediaDirection: 'next',
        likeCount: 0,
      }))

      const postIds = posts.map((post) => post.id)
      if (postIds.length) {
        const { data: media } = await supabase
          .from('post_media')
          .select('post_id, image_url, image_path, sort_order, offset_x, offset_y')
          .in('post_id', postIds)
          .order('sort_order', { ascending: true })

        posts.forEach((post) => {
          const items = (media || []).filter((item) => item.post_id === post.id)
          const urls = items.map((item) =>
            item.image_path
              ? supabase.storage.from('posts').getPublicUrl(item.image_path).data?.publicUrl
              : item.image_url
          )
          post.mediaUrls = urls
          post.mediaOffsets = [
            post.mediaOffsets?.[0] || { x: 50, y: 50 },
            ...items.map((item) => ({
              x: Number.isFinite(item.offset_x) ? item.offset_x : 50,
              y: Number.isFinite(item.offset_y) ? item.offset_y : 50,
            })),
          ]
        })

        const { data: likes } = await supabase
          .from('post_likes')
          .select('post_id')
          .in('post_id', postIds)

        const likeCounts = new Map()
        ;(likes || []).forEach((like) => {
          likeCounts.set(like.post_id, (likeCounts.get(like.post_id) || 0) + 1)
        })
        posts.forEach((post) => {
          post.likeCount = likeCounts.get(post.id) || 0
        })
      }

      this.myPosts = posts
    },
    nextMedia(post) {
      post.mediaDirection = 'next'
      post.mediaIndex = (post.mediaIndex + 1) % post.mediaUrls.length
    },
    prevMedia(post) {
      post.mediaDirection = 'prev'
      post.mediaIndex = (post.mediaIndex - 1 + post.mediaUrls.length) % post.mediaUrls.length
    },
    async saveProfile() {
      this.isSaving = true
      this.errorMessage = ''
      this.statusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.errorMessage = 'Please sign in again to update your profile.'
        this.isSaving = false
        return
      }

      const { error } = await supabase
        .from('profiles')
        .upsert(
          {
            id: userId,
            handle: this.handle || userId.slice(0, 8),
            full_name: this.fullName,
            bio: this.bio,
            avatar_url: this.avatarUrl,
            avatar_path: this.avatarPath,
            country: this.country,
            city: this.city,
            instagram_handle: this.instagramHandle,
            tags: this.selectedTags,
            pinned_status: this.pinnedStatus,
          },
          { onConflict: 'id' }
        )

      this.isSaving = false

      if (error) {
        this.errorMessage = error.message
        return
      }

      this.statusMessage = 'Profile updated.'
    },
    async handleAvatarUpload(event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.errorMessage = ''
      this.statusMessage = ''

      const { data } = await supabase.auth.getSession()
      const userId = data?.session?.user?.id

      if (!userId) {
        this.errorMessage = 'Please sign in again to upload an image.'
        return
      }

      let uploadFile = file
      try {
        uploadFile = await this.convertImageToWebp(file, { maxWidth: 512 })
      } catch (error) {
        this.errorMessage = 'Photo processing failed. Try a different image.'
        return
      }

      if (this.avatarPreviewUrl) {
        URL.revokeObjectURL(this.avatarPreviewUrl)
      }
      this.avatarPreviewUrl = URL.createObjectURL(uploadFile)

      const filePath = `${userId}/avatar.webp`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, uploadFile, { upsert: true, contentType: 'image/webp' })

      if (uploadError) {
        this.errorMessage = 'Upload failed. Make sure the "avatars" bucket exists.'
        return
      }

      const { data: publicUrl } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      this.avatarPath = filePath
      const baseUrl = publicUrl?.publicUrl || ''
      this.avatarUrl = baseUrl

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          avatar_url: baseUrl,
          avatar_path: this.avatarPath,
        })
        .eq('id', userId)

      if (profileError) {
        this.errorMessage = profileError.message || 'Unable to save profile photo.'
        return
      }

      window.dispatchEvent(
        new CustomEvent('profile:updated', {
          detail: { avatar_path: this.avatarPath, avatar_url: this.avatarUrl },
        })
      )
      if (this.avatarPreviewUrl) {
        URL.revokeObjectURL(this.avatarPreviewUrl)
      }
      this.avatarPreviewUrl = ''
      this.statusMessage = 'Photo updated.'
    },

    async deletePost(post) {
      const { error } = await supabase.from('posts').delete().eq('id', post.id)
      if (error) {
        this.errorMessage = error.message
        return
      }
      this.myPosts = this.myPosts.filter((item) => item.id !== post.id)
    },
    async confirmDeletePost() {
      if (!this.postToDelete) {
        this.showDeletePostConfirm = false
        return
      }
      await this.deletePost(this.postToDelete)
      this.postToDelete = null
      this.showDeletePostConfirm = false
    },
    async deleteProfile() {
      this.isDeleting = true
      this.errorMessage = ''
      this.statusMessage = ''

      const { data } = await supabase.auth.getSession()
      const session = data?.session
      const userId = session?.user?.id

      if (!userId) {
        this.errorMessage = 'Please sign in again to delete your profile.'
        this.isDeleting = false
        return
      }

      try {
        // Delete user's posts and related data first
        await supabase.from('posts').delete().eq('user_id', userId)
        
        // Delete user's profile
        await supabase.from('profiles').delete().eq('id', userId)
        
        // Sign out and redirect
        await supabase.auth.signOut()
        this.isDeleting = false
        this.showDeleteConfirm = false
        window.location.href = '/'
      } catch (error) {
        this.errorMessage = error.message || 'Unable to delete profile.'
        this.isDeleting = false
      }
    },
  },
}
</script>

<style>
.media-swipe-next-enter-active,
.media-swipe-next-leave-active,
.media-swipe-prev-enter-active,
.media-swipe-prev-leave-active {
  transition: opacity 220ms ease, transform 220ms ease;
}

.media-swipe-next-enter-from {
  opacity: 0;
  transform: translateX(10%);
}

.media-swipe-next-leave-to {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-enter-from {
  opacity: 0;
  transform: translateX(-10%);
}

.media-swipe-prev-leave-to {
  opacity: 0;
  transform: translateX(10%);
}
</style>
